import Header from "../components/Header";

const LAST_UPDATED = "8 May 2026";

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-2xl font-black text-[#ffd978] mb-3">{title}</h2>
      <div className="space-y-3 text-white/75 leading-relaxed">{children}</div>
    </div>
  );
}

function List({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

export default function TermsOfService() {
  return (
    <main className="bg-[#050505] text-white">
      <Header />

      <section className="mx-auto max-w-4xl px-6 pt-36 pb-20 select-text">
        <p className="font-black uppercase text-[#ffb56f]">Legal</p>
        <h1 className="outfall-heading text-[clamp(48px,8vw,90px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
          Terms of Service
        </h1>
        <p className="mt-4 text-white/40 font-bold">
          Last updated: {LAST_UPDATED}
        </p>

        <div className="mt-12 space-y-10">
          <p className="text-white/70 leading-relaxed">
            These Terms of Service ("Terms") govern your access to and use of
            the Outfall Roblox experience, website (
            <strong className="text-white/90">outfall.website</strong>,{" "}
            <strong className="text-white/90">outfall.co.uk</strong>), Discord
            community, and all related services (collectively, "the Services").
            By accessing or using any of the Services, you agree to be bound by
            these Terms. If you do not agree, do not use the Services.
          </p>

          <Section title="1. Eligibility">
            <p>
              You must be at least 13 years old to use our website and
              Discord-linked features. Use of the Roblox experience is
              additionally subject to Roblox's own age requirements. By using
              the Services, you confirm that you meet these requirements or that
              a parent or guardian has consented on your behalf.
            </p>
          </Section>

          <Section title="2. Use of the Roblox Experience">
            <p>
              Outfall is a Roblox experience operated independently by the
              Outfall team. By playing, you also agree to comply with:
            </p>
            <List
              items={[
                "Roblox Terms of Use",
                "Roblox Community Standards",
                "Roblox Safety and Civility Policy",
              ]}
            />
            <p>
              Outfall is provided free of charge. Any Roblox currency spent
              within the experience is subject to Roblox's own policies and we
              cannot issue refunds for Roblox transactions.
            </p>
          </Section>

          <Section title="3. Player Conduct">
            <p>
              When using Outfall's game or community platforms, you agree not
              to:
            </p>
            <List
              items={[
                "Cheat, exploit bugs, use third-party scripts, or gain unfair advantages",
                "Harass, threaten, or abuse other players or staff",
                "Use discriminatory, hateful, or offensive language",
                "Attempt to impersonate Outfall staff or other players",
                "Share, distribute, or promote cheating tools or exploits",
                "Attempt to access systems or data you are not authorised to access",
                "Coordinate or encourage others to violate these Terms",
              ]}
            />
            <p>
              Violations may result in warnings, temporary restrictions,
              permanent bans, or referral to platform authorities depending on
              severity.
            </p>
          </Section>

          <Section title="4. Website and Account Access">
            <p>
              Certain features of our website (career applications, ban appeals,
              application status) require you to authenticate using a Discord
              account via OAuth2. By doing so you authorise us to access the
              Discord profile information described in our Privacy Policy.
            </p>
            <p>
              You are responsible for maintaining the security of your Discord
              account. We are not liable for any loss resulting from
              unauthorised access to your account.
            </p>
            <p>
              We reserve the right to suspend or terminate your access to
              website features at any time, for any reason, including violation
              of these Terms.
            </p>
          </Section>

          <Section title="5. Career Applications">
            <p>
              Submitting a career application does not guarantee employment, a
              staff position, or any compensation. We reserve the right to
              accept or reject any application at our sole discretion without
              obligation to provide a reason. All information submitted in an
              application must be truthful and accurate. Submitting false or
              misleading information will result in immediate disqualification
              and may result in a community ban.
            </p>
          </Section>

          <Section title="6. Ban Appeals">
            <p>
              Players who have been banned from Outfall may submit a ban appeal
              through our website. Submitting an appeal does not guarantee
              reinstatement. The following rules apply:
            </p>
            <List
              items={[
                "You may only have one active (pending) appeal at a time",
                "All information in an appeal must be truthful — false appeals will be permanently denied",
                "Staff decisions on appeals are final unless new evidence is presented",
                "Attempting to circumvent a ban while an appeal is pending will result in permanent denial",
                "Abusive or threatening language in an appeal will result in immediate denial",
              ]}
            />
          </Section>

          <Section title="7. Intellectual Property">
            <p>
              All original content associated with Outfall — including game
              assets, the website design, logos, branding, and written content —
              is the property of the Outfall team. You may not reproduce,
              distribute, or create derivative works without explicit written
              permission.
            </p>
            <p>
              User-submitted content (application responses, appeal text)
              remains owned by the submitter, but by submitting you grant us a
              non-exclusive licence to store and use that content for the
              purpose of operating our services.
            </p>
          </Section>

          <Section title="8. In-Game Items and Progression">
            <p>
              Any in-game items, currency, cosmetics, ranks, or progression data
              in Outfall are provided subject to these Terms and remain under
              our control at all times. We reserve the right to modify, reset,
              or remove any in-game data, items, or features at any time without
              notice or compensation.
            </p>
          </Section>

          <Section title="9. Service Availability">
            <p>
              We do not guarantee uninterrupted access to any of the Services.
              We may suspend, modify, or discontinue any part of the Services at
              any time for maintenance, improvement, legal compliance, or any
              other reason. We will make reasonable efforts to notify users of
              planned downtime through our Discord server.
            </p>
          </Section>

          <Section title="10. Disclaimers and Limitation of Liability">
            <p>
              The Services are provided "as is" and "as available" without
              warranties of any kind, express or implied. To the maximum extent
              permitted by applicable law:
            </p>
            <List
              items={[
                "We do not warrant that the Services will be error-free, secure, or uninterrupted",
                "We are not liable for any loss of progress, data, items, or access",
                "We are not liable for any indirect, incidental, or consequential damages arising from use of the Services",
                "Our total liability to you for any claim shall not exceed £50",
              ]}
            />
          </Section>

          <Section title="11. Indemnification">
            <p>
              You agree to indemnify and hold harmless the Outfall team from any
              claims, damages, losses, or expenses (including legal fees)
              arising from your violation of these Terms or misuse of the
              Services.
            </p>
          </Section>

          <Section title="12. Governing Law">
            <p>
              These Terms are governed by and construed in accordance with the
              laws of England and Wales. Any disputes arising from these Terms
              shall be subject to the exclusive jurisdiction of the courts of
              England and Wales.
            </p>
          </Section>

          <Section title="13. Changes to These Terms">
            <p>
              We may update these Terms at any time. The "Last updated" date at
              the top of this page reflects the most recent revision. We will
              notify users of significant changes through our Discord server.
              Continued use of the Services after any update constitutes your
              acceptance of the revised Terms.
            </p>
          </Section>

          <Section title="14. Contact">
            <p>
              If you have questions about these Terms, contact us through our
              official Discord server or the contact channels listed at{" "}
              <strong className="text-white/90">outfall.website</strong>.
            </p>
          </Section>
        </div>
      </section>
    </main>
  );
}
